import { ReactNode, useEffect, useState } from 'react'

import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import MainLoader from '@components/MainLoader/MainLoader'
import useLogin from '@hooks/useLogin'
import AuthUtils from '@utils/AuthUtils'
import Login from '@views/auth/Login/Login'

const QA_TOKEN_KEY = 'qa_auth_token'

export default function SsoAuthenticationHandler({ children }: { children: ReactNode }) {
	const [accessToken, setAccessToken] = useState<string | undefined>(() => {
		const qaToken = localStorage.getItem(QA_TOKEN_KEY)

		if (qaToken) {
			AuthUtils.setSsoToken(qaToken)
			// eslint-disable-next-line no-console
			console.info('QA Auto Mode')
			return qaToken
		}
		return undefined
	})
	const isAuthenticated = useIsAuthenticated()
	const { instance, accounts, inProgress } = useMsal()

	const { mutate: login } = useLogin()

	useEffect(() => {
		if (localStorage.getItem(QA_TOKEN_KEY)) return

		// If the user is already authenticated, we can skip the SSO flow. We need to check if we already
		// have a token in local storage, because the user might have logged in previously.
		// This if() was added to fix a bug where:
		// 1. The user was logged in
		// 2. The vpn disconnected
		// 3. refreshing that page would get stuck on the mainLoader
		// The only way to fix it was to clear local storage, which removed the token and the user would have to login again.
		if (isAuthenticated && accounts.length > 0) {
			const token = AuthUtils.getSsoToken()
			if (token) {
				setAccessToken(token)
				login(token)
				return
			}
		}

		// This is old code and I'm not sure why we check to see if there is account data to get the token. :(
		//
		if (accounts.length > 0) {
			const request = {
				scopes: ['User.Read'],
				account: accounts[0],
			}
			instance
				.acquireTokenSilent(request)
				.then((response) => {
					setAccessToken(response.accessToken)
					AuthUtils.setSsoToken(response.accessToken)
					AuthUtils.setSsoAccount(accounts[0])
					// Note: I think it's ok to let this fail silently. We don't need to handle the asyncyness.
					// All it does is inform the BE that the user has logged in.

					login(response.accessToken)
				})
				.catch((error) => {
					// acquireTokenSilent can fail for a number of reasons, fallback to interaction
					if (error instanceof InteractionRequiredAuthError) {
						instance.acquireTokenPopup(request).then((response) => {
							setAccessToken(response.accessToken)
							AuthUtils.setSsoToken(response.accessToken)
							AuthUtils.setSsoAccount(accounts[0])

							login(response.accessToken)
						})
					}
					// Ignore other errors.
					// The msal library has to initialize before calling acquireTokenSilent so this will
					// error until that completes. The inProgress state will be set to 'none' when that is
					// done and this will rerun and get the token..
				})
		}
	}, [instance, accounts, inProgress, login, isAuthenticated])

	if (localStorage.getItem(QA_TOKEN_KEY) && accessToken) {
		return children
	}

	if (!isAuthenticated && inProgress === 'none') {
		// Logged out
		return <Login />
	}

	if (accessToken && accounts) {
		// Logged in
		return children
	}

	// Waiting for authentication
	return <MainLoader />
}
